import { defineComponent as _defineComponent } from 'vue'
import { ref, watch } from 'vue';
import { useContext } from '@nuxtjs/composition-api';
import redeemTokenCode from '~/mutations/redeemTokenCode.gql';
import isQualified from '~/queries/isQualified.gql';
import { useStore } from '~/store/composable';
import { EExchangeState, IExchangeReward } from '~/store/customReveal/types';
import { IBaseGqlResponse } from '~/store/types';
import { isRestrictionSuccess } from '~/utils/parseRestrictionResults';
import { rewards as mockRewards } from '~/mocks/exchange_rewards';
import CustomExchangeReveal from './CustomExchangeReveal.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'CustomExchangePromoCode',
  props: {
    code: null,
    logo: null,
    logoAlt: null,
    showErrors: { type: Boolean, default: true },
    video: null,
    mock: null
  },
  emits: ["close", "exchange", "exchange-complete", "exchange-error", "reset", "user-qualified"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'close'): void;
  (e: 'exchange'): void;
  (e: 'exchange-complete'): void;
  (e: 'exchange-error', message: string): void;
  (e: 'reset'): void;
  (e: 'user-qualified', isQualified: boolean, message?: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { code: string, logo?: string, logoAlt?: string, showErrors: boolean, video: string, mock?:
      | 'success'
      | 'restrictionsError'
      | 'userRedeemedError'
      | 'exchangeError' };





const {
  app: { apolloProvider },
  $ua,
  $sentry,
} = useContext();
const { state, dispatch, commit } = useStore();
const canExchange = ref(true);
const hasAutoStarted = ref(false);

const isUserQualified = async () => {
  if (!apolloProvider?.defaultClient) {
    return;
  }

  try {
    const userId = state.profile?.user?.id;
    if (!userId) {
      throw new Error('You must be logged in to redeem a box');
    }
    if (props.mock) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    const res = props.mock
      ? {
          data: {
            isQualified: {
              success:
                props.mock === 'success' || props.mock === 'exchangeError'
                  ? true
                  : false,
              message:
                props.mock === 'restrictionsError'
                  ? 'You are not eligible to redeem this Mystery Box'
                  : props.mock === 'userRedeemedError'
                  ? 'Your free Mystery Box has already been redeemed'
                  : '',
            },
          },
        }
      : await apolloProvider.defaultClient.query({
          query: isQualified,
          variables: {
            id: 'IS_REDEEMABLE_REDEMPTION_CODE',
            payload: {
              userId,
              redemptionCode: props.code,
            },
          },
          fetchPolicy: 'network-only',
        });
    return res?.data;
  } catch (e) {
    console.error(e);
  }
};

const redeemCode = async (code: string) => {
  if (!apolloProvider?.clients.gateway) {
    return;
  }

  try {
    dispatch(
      'customReveal/setExchangeState',
      EExchangeState.EXCHANGE_IN_FLIGHT,
    );

    if (props.mock) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    const { data } = props.mock
      ? {
          data: {
            redeemTokenCode: {
              success: props.mock !== 'exchangeError' ? true : false,
              message:
                props.mock === 'exchangeError'
                  ? 'Unable to complete exchange'
                  : '',
              data: props.mock !== 'exchangeError' ? mockRewards : [],
            },
          },
        }
      : await apolloProvider.clients.gateway.mutate<
          IBaseGqlResponse<'redeemTokenCode', { data?: IExchangeReward[] }>
        >({
          mutation: redeemTokenCode,
          variables: {
            code,
          },
        });

    if (data?.redeemTokenCode?.success) {
      // UA tracking
      $ua.trackRedeemTokenCodeCompleteEvent({
        tokenRedeemedType: 'exchange',
        tokenRedeemedSuccessfully: true,
      });

      return {
        response: {
          rewards: data?.redeemTokenCode.data,
        },
      };
    } else {
      // UA tracking
      $ua.trackRedeemTokenCodeErrorEvent({
        message: data?.redeemTokenCode.message,
        redeemTokenCode: data?.redeemTokenCode,
      });

      throw new Error(data?.redeemTokenCode?.message);
    }
  } catch (error) {
    // UA tracking
    $ua.trackRedeemTokenCodeErrorEvent(error);
    $sentry.captureException(error);
    throw new Error((error as Error).message ?? 'Something went wrong');
  }
};

const onExchangeComplete = (rewards?: IExchangeReward[]) => {
  dispatch('customReveal/setRewards', rewards ?? []);
  dispatch('customReveal/setExchangeState', EExchangeState.EXCHANGE_COMPLETE);
  emit('exchange-complete');
};

const onExchangeError = (message?: string) => {
  const errorMessage = message ?? 'Something went wrong';
  dispatch('customReveal/setExchangeState', EExchangeState.NOT_EXCHANGING);
  toggleError(errorMessage);
  emit('exchange-error', errorMessage);
};

const toggleError = (message: string) => {
  if (!props.showErrors) {
    return;
  }
  commit('updateSnackbarErrorText', message);
  commit('toggleErrorSnackbar');
};

const handleExchange = async () => {
  emit('exchange');
  const isQualifiedData = await isUserQualified();
  if (!isQualifiedData || !isRestrictionSuccess(isQualifiedData.isQualified)) {
    const errorMessage =
      isQualifiedData?.isQualified?.message ??
      'You are not eligible to redeem this Mystery Box';
    canExchange.value = false;
    toggleError(errorMessage);
    emit('user-qualified', false, errorMessage);
    return;
  }
  emit('user-qualified', true);
  try {
    const redeemCodeData = await redeemCode(props.code);
    if (redeemCodeData) {
      onExchangeComplete(redeemCodeData?.response?.rewards);
    } else {
      throw new Error('Something went wrong');
    }
  } catch (error) {
    console.warn(error);
    onExchangeError((error as Error).message);
  }
};

const reset = () => {
  hasAutoStarted.value = false;
  canExchange.value = true;
  emit('reset');
};

expose({
  handleExchange,
  reset,
});

watch(
  () => props.video,
  (current, previous) => {
    if (current && current !== previous) {
      dispatch('customReveal/setVideoReady', false);
      dispatch('customReveal/setVideoBoxType', current);
    }
  },
  { immediate: true },
);

watch(
  () => props.code,
  (current, previous) => {
    if (current && current !== previous) {
      dispatch('customReveal/setExchangeState', EExchangeState.NOT_EXCHANGING);
      dispatch('customReveal/setRewards', []);
      canExchange.value = true;
    }
  },
);

return { __sfc: true,props, emit, apolloProvider, $ua, $sentry, state, dispatch, commit, canExchange, hasAutoStarted, isUserQualified, redeemCode, onExchangeComplete, onExchangeError, toggleError, handleExchange, reset, CustomExchangeReveal }
}

})